// shared/src/news/news-dash-bundle.ts
// The /oracle "News" tab read contract (task #75, Shape B-flat — design doc §8, locked).
// A UNION-typed news surface over two D1 tables:
//   · news_items          — the deduped WINNERS (migrations 0082 + 0103), grouped by
//     scope, ranked cross_vendor_hit DESC → tier A<B<C → published_at DESC.
//   · news_processing_log — a FLAT tab-level "Deduped & Rejected" audit panel
//     (migration 0011). The log is positional/no-FK to news_items, so a per-item drill
//     would misattribute a dedup decision to the wrong survivor — this is deliberately a
//     flat list, never nested per group/item.
// Defined ONCE here (mirrors macro-dash-bundle.ts / relations-dash-bundle.ts) so the
// api-worker projector and the apps/web tab share it verbatim (no C035 hand-mirror). The
// projector runs the SQL; this PURE assembler shapes + sorts + caps it.

import { z } from 'zod';
import type { NewsItemRow } from './news-items-store.js';
import type { BriefSliceItem, SourceCiteRate } from './news-relevance-store.js';
import type { NewsProcessingLogRow } from './types.js';
import { classifyHeuristic, type NewsAssetClass } from './classify.js';
import { newsRankScore, DEFAULT_NEWS_RANK_WEIGHTS, type NewsRankWeights } from './rank.js';
import { safeParseTagArray } from './resolve-tags.js';
import type { NewsStance } from './derive-impact.js';

// ─────────────────────────── view schemas ───────────────────────────

export const OracleNewsItemViewSchema = z.object({
  id:               z.string(),
  title:            z.string(),
  url:              z.string(),
  source_domain:    z.string(),
  published_at:     z.string(),
  tier:             z.enum(['A', 'B', 'C']).nullable(),
  cross_vendor_hit: z.boolean(),
  pipeline:         z.string(),
  scope:            z.string(),
  topic:            z.string().nullable(),
  // Central news pool P0 intrinsic tags (0120) — the entity/theme chips the oracle News tab renders.
  // Parsed from the persisted JSON columns; [] / null when a row predates the tagger + its backfill.
  entity_tickers:   z.array(z.string()),
  themes:           z.array(z.string()),
  salience:         z.number().nullable(),
  // Central news pool P2c-2 provenance — tickers whose per-ticker slice CITED this item in the latest
  // materialized brief (news_relevance.cited=1). [] when uncited / no brief has run since P2a. This is
  // the exact "which analysts were shown this, and used it" overlay the F-tab surfaces.
  cited_by:         z.array(z.string()),
  // Per-stance overlay (2026-07-28) — across the tickers that cited this item in the latest brief, the
  // max score each analyst STANCE gave it (only citing stances present). Companion to cited_by (which
  // tickers). {} when uncited / no brief / api predates it. Derived compute-on-read from that brief's
  // llm_calls (the persisted slice carries no stance dimension).
  cited_stances:    z.object({
    consensus:  z.number().int().min(1).max(2).optional(),
    contrarian: z.number().int().min(1).max(2).optional(),
    arbiter:    z.number().int().min(1).max(2).optional(),
  }).default({}),
});

export const OracleNewsGroupViewSchema = z.object({
  scope:      z.string(),
  item_count: z.number(),
  items:      z.array(OracleNewsItemViewSchema),
});

// #89: the TOPICAL SUMMARY strip — the same asset-class clustering the ops News tab shows,
// computed here from the deterministic heuristic classifier (title+summary+domain) rather
// than the stored `topic` column (which is NULL for ~all rows until the classify backfill
// runs). One entry per asset class present in the window, item_count DESC.
export const OracleNewsTopicViewSchema = z.object({
  asset_class:  z.string(),   // NewsAssetClass — crypto|stocks|bonds_rates|oil_energy|fx|macro
  item_count:   z.number(),
  source_count: z.number(),   // distinct source_domains in this class
  top_titles:   z.array(z.string()),  // newest N headlines, cleaned
});

export const OracleNewsDedupRowViewSchema = z.object({
  decision:       z.string(),
  vendor_a:       z.string().nullable(),
  vendor_b:       z.string().nullable(),
  jaccard:        z.number().nullable(),
  dropped_domain: z.string().nullable(),
  dropped_tier:   z.string().nullable(),
  title_a:        z.string().nullable(),
  title_b:        z.string().nullable(),
});

// ── P2c-2 provenance overlay: the latest brief's per-ticker slice, from news_relevance ──

// One (news_item_id) row inside a target-ticker's slice — what THAT ticker's analysts were shown.
export const OracleNewsSliceItemViewSchema = z.object({
  news_item_id: z.string(),
  title:        z.string(),
  source:       z.enum(['direct', 'peer', 'driver', 'question']),   // the relevance layer that pulled it
  cited:        z.boolean(),
});

// The by-target lens — one entry per ticker in the latest brief's materialized slice, cited_count DESC.
export const OracleNewsByTargetViewSchema = z.object({
  ticker:       z.string(),
  item_count:   z.number(),
  cited_count:  z.number(),
  items:        z.array(OracleNewsSliceItemViewSchema),
});

// Per-relevance-layer cite-rate over the analytics window — the empirical utility signal (all four
// layers always present; total 0 = no data yet → rate 0, neutral).
export const OracleNewsCiteRateViewSchema = z.object({
  source: z.enum(['direct', 'peer', 'driver', 'question']),
  total:  z.number(),
  cited:  z.number(),
  rate:   z.number(),
});

export const OracleNewsRelevanceViewSchema = z.object({
  brief_id:   z.string().nullable(),   // the latest brief that materialized a slice (null → none yet)
  by_target:  z.array(OracleNewsByTargetViewSchema),
  cite_rate:  z.array(OracleNewsCiteRateViewSchema),
});

export const OracleNewsBundleSchema = z.object({
  generated_at: z.string(),
  window_hours: z.number(),
  topics:       z.array(OracleNewsTopicViewSchema),
  groups:       z.array(OracleNewsGroupViewSchema),
  relevance:    OracleNewsRelevanceViewSchema,
  dedup: z.object({
    total: z.number(),
    rows:  z.array(OracleNewsDedupRowViewSchema),
  }),
});

export type OracleNewsItemView     = z.infer<typeof OracleNewsItemViewSchema>;
export type OracleNewsTopicView    = z.infer<typeof OracleNewsTopicViewSchema>;
export type OracleNewsGroupView    = z.infer<typeof OracleNewsGroupViewSchema>;
export type OracleNewsSliceItemView = z.infer<typeof OracleNewsSliceItemViewSchema>;
export type OracleNewsByTargetView = z.infer<typeof OracleNewsByTargetViewSchema>;
export type OracleNewsCiteRateView = z.infer<typeof OracleNewsCiteRateViewSchema>;
export type OracleNewsRelevanceView = z.infer<typeof OracleNewsRelevanceViewSchema>;
export type OracleNewsDedupRow     = z.infer<typeof OracleNewsDedupRowViewSchema>;
export type OracleNewsBundle       = z.infer<typeof OracleNewsBundleSchema>;

// ─────────────────────────── pure assembler ───────────────────────────

const MAX_ITEMS_PER_GROUP = 20;
const MAX_GROUPS = 50;
const TOPIC_TITLES_CAP = 3;   // newest headlines shown per asset-class cluster


/** Strip residual HTML markup + decode common entities + collapse whitespace. RSS titles
 *  are already cleaned at ingestion (refresh-fetcher's rewrite-cf-tools.ts parser), but
 *  'search'-pipeline (GDELT/Polygon) titles pass through raw — this is the one place every
 *  title crosses on its way to the tab, so it is the right chokepoint to guarantee clean
 *  text regardless of which pipeline produced the row. */
export function cleanNewsTitle(raw: string): string {
  // TWO passes: the feeds mix single-encoding (tags as &lt;b&gt;) with double-encoding
  // (apostrophes as &amp;#39;), so a single decode leaves a residual entity. Two bounded
  // passes reach a fixed point for the depths seen in production without an unbounded loop.
  let s = raw;
  for (let pass = 0; pass < 2; pass++) {
    s = s
      // Strip HTML tags in BOTH literal (<b>) and entity-encoded (&lt;b&gt;) form. The encoded
      // strip MUST run before &lt;/&gt; are decoded, else decoding re-creates a <b> the literal
      // strip already passed. Both require a letter/'/' right after the opener, so a bare
      // "5 < 10" / "20 &gt; 15" comparison is never mistaken for a tag and eaten.
      .replace(/<\/?[a-zA-Z][^>]*>/g, '')
      .replace(/&lt;\/?[a-zA-Z][\s\S]*?&gt;/g, '')
      // Numeric character references (hex &#x2019; / decimal &#8217;), guarding an out-of-range
      // code point by leaving the original entity untouched rather than throwing.
      .replace(/&#x([0-9a-fA-F]+);/g, (m, h: string) => cpOrRaw(parseInt(h, 16), m))
      .replace(/&#(\d+);/g,           (m, d: string) => cpOrRaw(parseInt(d, 10), m))
      // Named entities (incl. &apos;, which the RSS/GDELT feeds emit and the DOM would decode).
      .replace(/&apos;/g, "'").replace(/&quot;/g, '"')
      .replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&');   // last, so it can't manufacture a false entity within a pass
  }
  return s.replace(/\s+/g, ' ').trim();
}

/** A decoded code point, or the original entity text when the value is out of the Unicode range
 *  (String.fromCodePoint throws otherwise — a malformed feed must not blow up the whole bundle). */
function cpOrRaw(cp: number, original: string): string {
  return Number.isFinite(cp) && cp >= 0 && cp <= 0x10ffff ? String.fromCodePoint(cp) : original;
}

export interface NewsAssembleInput {
  readonly generatedAt: string;
  readonly windowHours: number;
  readonly items:       ReadonlyArray<NewsItemRow>;      // news_items survivors in the window
  readonly dedupRows:   ReadonlyArray<NewsProcessingLogRow>; // news_processing_log dedup/reject decisions, at_iso DESC
  readonly rankWeights?: NewsRankWeights;                // #96: live oracle_news_policy weights (default = seed)
  // P2c-2 provenance — the latest brief's materialized per-ticker slice + cite-rates (news_relevance).
  // Omitted (undefined) before the api-worker threads them → an empty overlay (brief_id null), never an
  // error: the tab degrades to the pre-P2c-2 view until the first brief materializes a slice.
  readonly relevanceBriefId?: string | null;
  readonly sliceItems?:      ReadonlyArray<BriefSliceItem>;
  readonly citeRates?:       ReadonlyArray<SourceCiteRate>;
  // Per-stance overlay: news_item_id → max score each stance gave it across the citing tickers, in the
  // latest brief (derived by the api projector from that brief's llm_calls). Omitted → empty overlay.
  readonly citedStancesByItemId?: ReadonlyMap<string, Partial<Record<NewsStance, 1 | 2>>>;
}

const MAX_SLICE_ITEMS_PER_TARGET = 40;   // dense per-ticker list cap (the F-tab by-target lens)

export function assembleNewsOracleBundle(input: NewsAssembleInput): OracleNewsBundle {
  const { generatedAt, windowHours, items, dedupRows } = input;
  // #96: display ranking uses the same weighted score as the LLM ranking (rank.ts). Defaults to the
  // seed weights (behavior-identical to the prior cross_vendor→tier→recency sort) until the api-worker
  // builder threads the live oracle_news_policy '*' row.
  const rankWeights: NewsRankWeights = input.rankWeights ?? DEFAULT_NEWS_RANK_WEIGHTS;

  // ── P2c-2 provenance overlay from the latest brief's slice (news_relevance) ──
  const sliceItems = input.sliceItems ?? [];
  // news_item_id → sorted-unique tickers that CITED it in the latest brief (cited=1). The per-item
  // cited_by badge. A slice row with cited=0 contributes to by_target but NOT here.
  const citedByMap = new Map<string, Set<string>>();
  for (const s of sliceItems) {
    if (s.cited !== 1) continue;
    let set = citedByMap.get(s.news_item_id);
    if (!set) { set = new Set(); citedByMap.set(s.news_item_id, set); }
    set.add(s.ticker);
  }
  const citedByFor = (id: string): string[] => {
    const set = citedByMap.get(id);
    return set ? [...set].sort() : [];
  };

  // ── #89 topical summary: cluster survivors by heuristic asset class ──
  // Same six-class strip the ops News tab shows, but classified on the fly from
  // title+summary+domain (the stored `topic` column is NULL for ~all rows). Pure/deterministic.
  const byTopic = new Map<NewsAssetClass, { rows: NewsItemRow[]; sources: Set<string> }>();
  for (const it of items) {
    const cls = classifyHeuristic({ title: it.title, summary: it.summary ?? '', source_domain: it.source_domain }).asset_class;
    let bucket = byTopic.get(cls);
    if (!bucket) { bucket = { rows: [], sources: new Set() }; byTopic.set(cls, bucket); }
    bucket.rows.push(it);
    bucket.sources.add(it.source_domain);
  }
  const topics: OracleNewsTopicView[] = [...byTopic.entries()]
    .map(([asset_class, b]): OracleNewsTopicView => {
      const newest = [...b.rows].sort((a, z) => z.published_at.localeCompare(a.published_at));
      return {
        asset_class,
        item_count:   b.rows.length,
        source_count: b.sources.size,
        top_titles:   newest.slice(0, TOPIC_TITLES_CAP).map((r) => cleanNewsTitle(r.title)),
      };
    })
    .sort((a, z) => z.item_count - a.item_count || a.asset_class.localeCompare(z.asset_class));

  // ── group survivors by scope ──
  const byScope = new Map<string, NewsItemRow[]>();
  for (const it of items) {
    const bucket = byScope.get(it.scope);
    if (bucket) bucket.push(it); else byScope.set(it.scope, [it]);
  }

  const groups: OracleNewsGroupView[] = [...byScope.entries()]
    .map(([scope, rows]): OracleNewsGroupView => {
      // Rank by the weighted news-policy score (tier weight + cross-vendor boost), newer first within
      // a score-class. Same formula the LLM ranking uses (rank.ts newsRankScore).
      const sorted = [...rows].sort((a, b) => {
        const sa = newsRankScore({ source_tier: a.tier, cross_vendor_hit: a.cross_vendor_hit === 1 }, rankWeights);
        const sb = newsRankScore({ source_tier: b.tier, cross_vendor_hit: b.cross_vendor_hit === 1 }, rankWeights);
        if (sa !== sb) return sb - sa;
        return b.published_at.localeCompare(a.published_at);
      });
      return {
        scope,
        item_count: rows.length,   // the FULL scope count, even though items[] is capped below
        items: sorted.slice(0, MAX_ITEMS_PER_GROUP).map((r): OracleNewsItemView => ({
          id:               r.id,
          title:            cleanNewsTitle(r.title),
          url:              r.url,
          source_domain:    r.source_domain,
          published_at:     r.published_at,
          tier:             r.tier,
          cross_vendor_hit: r.cross_vendor_hit === 1,
          pipeline:         r.pipeline,
          scope:            r.scope,
          topic:            r.topic,
          entity_tickers:   safeParseTagArray(r.entity_tickers),
          themes:           safeParseTagArray(r.themes),
          salience:         r.salience,
          cited_by:         citedByFor(r.id),
          cited_stances:    input.citedStancesByItemId?.get(r.id) ?? {},
        })),
      };
    })
    .sort((a, b) => b.item_count - a.item_count || a.scope.localeCompare(b.scope))
    .slice(0, MAX_GROUPS);

  // ── P2c-2 by-target lens: group the latest brief's slice rows by ticker ──
  // sliceItems arrive ticker-then-score-desc (readBriefSliceItems ORDER BY), so per-ticker item order
  // is already the salience order the analysts saw. cited_count DESC then ticker for a stable lens order.
  const byTicker = new Map<string, BriefSliceItem[]>();
  for (const s of sliceItems) {
    const bucket = byTicker.get(s.ticker);
    if (bucket) bucket.push(s); else byTicker.set(s.ticker, [s]);
  }
  const by_target: OracleNewsByTargetView[] = [...byTicker.entries()]
    .map(([ticker, rows]): OracleNewsByTargetView => ({
      ticker,
      item_count:  rows.length,
      cited_count: rows.reduce((n, r) => n + (r.cited === 1 ? 1 : 0), 0),
      items: rows.slice(0, MAX_SLICE_ITEMS_PER_TARGET).map((r): OracleNewsSliceItemView => ({
        news_item_id: r.news_item_id,
        title:        cleanNewsTitle(r.title),
        source:       r.source,
        cited:        r.cited === 1,
      })),
    }))
    .sort((a, z) => z.cited_count - a.cited_count || z.item_count - a.item_count || a.ticker.localeCompare(z.ticker));

  const cite_rate: OracleNewsCiteRateView[] = (input.citeRates ?? []).map((c) => ({
    source: c.source, total: c.total, cited: c.cited, rate: c.rate,
  }));

  const relevance: OracleNewsRelevanceView = {
    brief_id: input.relevanceBriefId ?? null,
    by_target,
    cite_rate,
  };

  // ── the flat dedup/reject audit panel — NOT grouped, NOT joined to a survivor ──
  const dedup = {
    total: dedupRows.length,
    rows: dedupRows.map((r): OracleNewsDedupRow => ({
      decision:       r.decision,
      vendor_a:       r.vendor_a,
      vendor_b:       r.vendor_b,
      jaccard:        r.jaccard,
      dropped_domain: r.source_domain,
      dropped_tier:   r.assigned_tier,
      title_a:        r.title_a ? cleanNewsTitle(r.title_a) : r.title_a,
      title_b:        r.title_b ? cleanNewsTitle(r.title_b) : r.title_b,
    })),
  };

  return { generated_at: generatedAt, window_hours: windowHours, topics, groups, relevance, dedup };
}
